import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail, Users, Send, History, Plus, Search, FileText, Settings, Clock, BarChart3, Zap } from 'lucide-react'
import api, { getMyProspects } from '../services/api'
import toast from 'react-hot-toast'
import SendEmailView from '../components/communication/SendEmailView'
import ContactsList from '../components/communication/ContactsList'
import RecipientsList from '../components/communication/RecipientsList'
import EmailHistoryGrouped from '../components/communication/EmailHistoryGrouped'
import EmailTemplates from '../components/communication/EmailTemplates'
import EmailSignature from '../components/communication/EmailSignature'
import ScheduledEmails from '../components/communication/ScheduledEmails'
import EmailTrackingStats from '../components/communication/EmailTrackingStats'
import EmailSequences from '../components/communication/EmailSequences'
import SequenceProspectTracker from '../components/communication/SequenceProspectTracker'
import '../styles/communication.css'

// Configuration des onglets
const TABS = [
  { id: 'send', label: 'Envoyer', icon: Mail },
  { id: 'contacts', label: 'Contacts', icon: Users, showCount: true },
  { id: 'recipients', label: 'Destinataires', icon: Users },
  { id: 'sequences', label: 'Séquences', icon: Zap, variant: 'green' },
  { id: 'prospect-tracker', label: 'Suivi Prospects', icon: Users, variant: 'blue' },
  { id: 'templates', label: 'Templates', icon: FileText },
  { id: 'history', label: 'Historique', icon: History },
  { id: 'tracking', label: 'Tracking', icon: BarChart3 },
  { id: 'scheduled', label: 'Planifiés', icon: Clock, variant: 'orange' },
  { id: 'settings', label: 'Paramètres', icon: Settings },
]

export default function Communication() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('send')
  const [searchQuery, setSearchQuery] = useState('')

  // Récupérer les prospects pour la liste de contacts
  const { data, isLoading: prospectsLoading } = useQuery({
    queryKey: ['myProspects'],
    queryFn: getMyProspects,
  })

  // Filtrer les prospects avec email
  const prospects = data?.prospects || []
  const contacts = prospects.filter(p => p.primaryEmail) || []
  
  // Filtrer par recherche
  const filteredContacts = contacts.filter(contact => {
    const search = searchQuery.toLowerCase()
    return (
      contact.primaryEmail?.toLowerCase().includes(search) ||
      contact.firstName?.toLowerCase().includes(search) ||
      contact.lastName?.toLowerCase().includes(search) ||
      contact.fullName?.toLowerCase().includes(search) ||
      contact.companyName?.toLowerCase().includes(search) ||
      contact.jobTitle?.toLowerCase().includes(search) ||
      contact.country?.toLowerCase().includes(search) ||
      contact.companyIndustry?.toLowerCase().includes(search)
    )
  })

  // Supprimer un contact
  const deleteContact = async (contactId) => {
    await api.delete(`/api/prospects/${contactId}`)
    queryClient.invalidateQueries(['myProspects'])
  }

  // Génère les classes CSS pour un onglet
  const getTabClassName = (tab) => {
    const classes = ['comm-tab']
    if (activeTab === tab.id) classes.push('active')
    if (tab.variant) classes.push(`variant-${tab.variant}`)
    return classes.join(' ')
  }

  return (
    <div className="view-container active" data-view="communication">
      {/* Header */}
      <div style={{
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 700,
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <Mail size={32} style={{ color: '#5b5cf4' }} />
            Communication
          </h1>
          <p style={{ color: '#888', fontSize: '1rem' }}>
            Gérez vos contacts et séquences email
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="comm-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={getTabClassName(tab)}
            >
              <Icon size={18} />
              {tab.label}
              {tab.showCount && (
                <span className="comm-tab-badge">{contacts.length}</span>
              )}
            </button>
          )
        })}
      </div>

      {/* Content */}
      {activeTab === 'send' && <SendEmailView />}

      {activeTab === 'contacts' && (
        <ContactsList 
          contacts={filteredContacts} 
          isLoading={prospectsLoading}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onDeleteContact={deleteContact}
        />
      )}

      {activeTab === 'recipients' && <RecipientsList />}

      {activeTab === 'templates' && <EmailTemplates />}

      {activeTab === 'sequences' && <EmailSequences />}

      {activeTab === 'prospect-tracker' && <SequenceProspectTracker />}

      {activeTab === 'history' && <EmailHistoryGrouped />}

      {activeTab === 'tracking' && <EmailTrackingStats />}

      {activeTab === 'scheduled' && <ScheduledEmails />}

      {activeTab === 'settings' && <EmailSignature />}
    </div>
  )
}
